import type { ColumnDef } from "@tanstack/react-table";

export interface ColumnRanking {
    highest: number | null;
    second: number | null;
    third: number | null;
    isLowerBetter: boolean;
}

function resolveAccessorKey(row: Record<string, unknown>, accessorKey: string): unknown {
    const keys = accessorKey.split(".");
    let result: unknown = row;
    for (const key of keys) {
        if (result && typeof result === "object") {
            result = (result as Record<string, unknown>)[key];
        } else {
            return null;
        }
    }
    return result;
}

function toNumeric(value: unknown): number | null {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
        const cleaned = value.replace(/[^0-9.-]/g, "");
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? null : parsed;
    }
    return null;
}

export function computeColumnRankings<TData>(
    data: TData[],
    columns: ColumnDef<TData>[],
    lowerIsBetterColumns: string[]
): Record<string, ColumnRanking> {
    const rankings: Record<string, ColumnRanking> = {};

    if (!data || data.length === 0) return rankings;

    columns.forEach((columnDef) => {
        const colDef = columnDef as {
            id?: string;
            accessorKey?: string;
            accessorFn?: (row: TData, index: number) => unknown;
        };

        const columnId = (colDef.accessorKey as string) || colDef.id || "";
        if (!columnId) return;

        const isLowerBetter = lowerIsBetterColumns.includes(columnId);

        const columnValues = data
            .map((row, index) => {
                let value: unknown;
                if (colDef.accessorFn) {
                    value = colDef.accessorFn(row, index);
                } else if (colDef.accessorKey) {
                    value = resolveAccessorKey(row as Record<string, unknown>, colDef.accessorKey as string);
                }
                return toNumeric(value);
            })
            .filter((value): value is number => value !== null && !isNaN(value) && value !== 0);

        if (columnValues.length === 0) return;

        const sortedValues = [...columnValues].sort((a, b) => (isLowerBetter ? a - b : b - a));
        const uniqueValues = [...new Set(sortedValues)];

        rankings[columnId] = {
            highest: uniqueValues[0] ?? null,
            second: uniqueValues[1] ?? null,
            third: uniqueValues[2] ?? null,
            isLowerBetter,
        };
    });

    return rankings;
}

export function getRankColor(
    value: unknown,
    columnId: string,
    rankings: Record<string, ColumnRanking>
): string | undefined {
    const numericValue = toNumeric(value);
    if (numericValue === null || numericValue === 0 || !rankings[columnId]) return undefined;

    const { highest, second, third } = rankings[columnId];
    const epsilon = 0.001;
    const matches = (a: number, b: number | null) => b !== null && Math.abs(a - b) < epsilon;

    if (matches(numericValue, highest)) return "var(--color-medal-gold)";
    if (matches(numericValue, second)) return "var(--color-rank-silver)";
    if (matches(numericValue, third)) return "var(--color-rank-bronze)";
    return undefined;
}

export function getSortDirection(header: {
    column: { getCanSort: () => boolean; getIsSorted: () => false | "asc" | "desc" };
}): false | "asc" | "desc" | null {
    if (!header.column.getCanSort()) return null;
    return header.column.getIsSorted();
}

export const formatValue = (value: number): string => {
    if (value === 0) return "-";
    return new Intl.NumberFormat("en-US").format(value);
};
